'use client';

import React, { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = document.querySelectorAll('.fade-in-up');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

interface UniversityCardProps {
  name: string;
  city: string;
  type: string;
  founded: string;
  ranking: string;
  programmes: string[];
  tuition: string;
  about: string;
}

const UniversityCard = ({ name, city, type, founded, ranking, programmes, tuition, about }: UniversityCardProps) => (
  <div
    className="fade-in-up hover-lift bg-white rounded-2xl overflow-hidden border"
    style={{ borderColor: '#e0d8c8' }}
  >
    <div className="p-8">
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="inline-block px-3 py-1 rounded-full text-xs font-body font-semibold uppercase tracking-wider" style={{ backgroundColor: '#e8f4f9', color: '#2A7FA5' }}>
          {type}
        </span>
        <span className="inline-block px-3 py-1 rounded-full text-xs font-body font-semibold uppercase tracking-wider" style={{ backgroundColor: '#F5EDD6', color: '#3d5566' }}>
          {city}
        </span>
      </div>
      <h3 className="font-headline font-bold mb-2" style={{ fontSize: '1.4rem', color: '#1a2e3b' }}>
        {name}
      </h3>
      <div className="flex flex-wrap gap-4 mb-4 text-sm font-body" style={{ color: '#5a6e7a' }}>
        <span>Founded {founded}</span>
        <span>·</span>
        <span>{ranking}</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        {programmes.map((p, i) => (
          <span key={i} className="inline-block px-2.5 py-1 rounded text-xs font-body font-medium" style={{ backgroundColor: '#fdfaf5', color: '#3d5566', border: '1px solid #e0d8c8' }}>
            {p}
          </span>
        ))}
      </div>
      <div className="mb-5 text-sm font-body font-medium" style={{ color: '#1a2e3b' }}>
        💰 {tuition}
      </div>
      <p className="font-body text-sm leading-relaxed" style={{ color: '#5a6e7a' }}>
        {about}
      </p>
    </div>
  </div>
);

const comparisonData = [
  { institution: 'UCM', city: 'Madrid', country: 'Spain', type: 'Public', focus: 'Law · Medicine · Business', tuition: '€6,000–14,000' },
  { institution: 'UAM', city: 'Madrid', country: 'Spain', type: 'Public Research', focus: 'AI · Economics · Sciences', tuition: '€7,000–15,000' },
  { institution: 'IE University', city: 'Madrid', country: 'Spain', type: 'Private', focus: 'MBA · Finance · Law', tuition: '€22,000–38,000' },
  { institution: 'Comillas', city: 'Madrid', country: 'Spain', type: 'Private Jesuit', focus: 'Business · Engineering · Law', tuition: '€8,000–16,000' },
  { institution: 'Universidad Europea', city: 'Madrid', country: 'Spain', type: 'Private', focus: 'Business · Sports · Health', tuition: '€9,000–18,000' },
  { institution: 'ESADE', city: 'Barcelona', country: 'Spain', type: 'Private', focus: 'MBA · Finance · Law', tuition: '€28,000–42,000' },
  { institution: 'Nova SBE', city: 'Lisbon', country: 'Portugal', type: 'Public', focus: 'Business · Finance · Data', tuition: '€10,000–22,000' },
  { institution: 'Nova School of Law', city: 'Lisbon', country: 'Portugal', type: 'Public', focus: 'LLM · EU Law · IP & Tech', tuition: '€8,000–16,000' },
  { institution: 'Universidade do Porto', city: 'Porto', country: 'Portugal', type: 'Public Research', focus: 'Engineering · CS · Business', tuition: '€6,500–15,000' },
];

export default function UniversitiesPortugalPage() {
  useScrollReveal();

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[480px] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/images/svetlana-gumerova-gOLCAOuc7iA-unsplash-1779197330178.jpg"
            alt="Lisbon iconic 25 de Abril suspension bridge over the Tagus river at golden hour"
            fill
            className="object-cover object-center"
            priority
            quality={90}
          />
          <div className="absolute inset-0 city-overlay" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 pb-16 pt-32">
          <p className="eyebrow text-white/80 mb-4">Universities</p>
          <h1 className="font-headline font-extrabold text-white" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Study in Portugal
          </h1>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 fade-in-up">
          <span className="accent-bar" />
          <p className="font-body leading-relaxed" style={{ fontSize: '1.2rem', color: '#3d5566' }}>
            Portugal is one of Europe's most welcoming destinations for international students — affordable tuition, a straightforward student visa process, and a 2-year post-study work permit that puts you on the path to EU residency. Our consultants work with three outstanding institutions across Lisbon and Porto.
          </p>
        </div>
      </section>

      {/* LISBON SECTION */}
      <section className="pb-24">
        <div className="relative h-64 mb-16 overflow-hidden">
          <Image
            src="/assets/images/svetlana-gumerova-gOLCAOuc7iA-unsplash-1779197330178.jpg"
            alt="Lisbon cityscape with the Tagus river, historic trams and terracotta rooftops"
            fill
            className="object-cover object-center"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(26,46,59,0.65)' }} />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="font-headline font-bold text-white" style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
                Lisbon
              </h2>
              <p className="font-body text-white/80 text-lg mt-2">Portugal's Capital · Europe's Sunniest City</p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          <UniversityCard
            name="Nova School of Business & Economics (Nova SBE)"
            city="Carcavelos, Lisbon"
            type="Public"
            founded="1978"
            ranking="FT Masters: Global Top 50"
            programmes={['Business & Management', 'Finance & FinTech', 'Economics', 'Data Science', 'Sustainability']}
            tuition="€10,000–22,000/year (EU rate: €3,000–6,000)"
            about="Portugal's highest-ranked business school and the only Portuguese institution in the FT Global Top 50. Our consultants consider Nova SBE a priority placement for students in Finance, Data Science, and Business — with a stunning beachside campus opened in 2021 and 94% employment within 3 months of graduation. Predominantly English."
          />
          <UniversityCard
            name="Nova School of Law"
            city="Campolide, Lisbon"
            type="Public (Nova University)"
            founded="1979"
            ranking="Portugal's Most International Law Faculty"
            programmes={['LLM International Business Law', 'EU Law', 'Human Rights', 'IP & Tech Law']}
            tuition="€8,000–16,000/year (EU rate: €2,500–4,500)"
            about="Portugal's most internationally regarded law faculty. Our consultants recommend Nova School of Law for Indian advocates and law graduates seeking EU legal qualifications — with 8+ English LLM programmes, Indian Bar compatibility, and Camões Institute scholarships available."
          />
        </div>
      </section>

      {/* PORTO SECTION */}
      <section className="pb-24" style={{ backgroundColor: '#fdfaf5' }}>
        <div className="relative h-64 mb-16 overflow-hidden">
          <Image
            src="/assets/images/andreas-brucker-X87yB-jvYHw-unsplash-1779197352455.jpg"
            alt="Porto historic Ribeira district with colourful buildings along the Douro river at sunset"
            fill
            className="object-cover object-center"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(26,46,59,0.65)' }} />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="font-headline font-bold text-white" style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
                Porto
              </h2>
              <p className="font-body text-white/80 text-lg mt-2">Portugal's Second City · Thriving Tech Hub</p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6">
          <UniversityCard
            name="Universidade do Porto"
            city="Porto"
            type="Public Research"
            founded="1911"
            ranking="QS: Top 300 Global"
            programmes={['Engineering & Tech', 'Data Science', 'Computer Science', 'Architecture', 'Business', 'Law']}
            tuition="€6,500–15,000/year (EU rate: €1,000–2,500)"
            about="Portugal's second-largest research university and the best-value EU tech degree for Indian engineering students. Our consultants highlight Porto's thriving tech district — home to Farfetch, Natixis, and hundreds of European employers hiring graduates directly — as a major advantage for software engineering, data science, and computing students. Living costs from €700/month."
          />
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 fade-in-up">
            <span className="eyebrow">All 9 Partner Universities</span>
            <h2 className="font-headline font-bold mt-3" style={{ color: '#1a2e3b' }}>
              University Comparison
            </h2>
          </div>
          <div className="fade-in-up overflow-x-auto rounded-2xl border" style={{ borderColor: '#e0d8c8' }}>
            <table className="w-full text-sm font-body">
              <thead>
                <tr style={{ backgroundColor: '#2A7FA5' }}>
                  {['Institution', 'City', 'Country', 'Type', 'Key Focus', 'Intl. Tuition/Year'].map((h) => (
                    <th key={h} className="px-5 py-4 text-left text-white font-semibold text-xs uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr
                    key={i}
                    className="border-t transition-colors duration-150 hover:bg-blue-50"
                    style={{ borderColor: '#e0d8c8', backgroundColor: i % 2 === 0 ? '#ffffff' : '#fdfaf5' }}
                  >
                    <td className="px-5 py-4 font-semibold" style={{ color: '#1a2e3b' }}>{row.institution}</td>
                    <td className="px-5 py-4" style={{ color: '#3d5566' }}>{row.city}</td>
                    <td className="px-5 py-4" style={{ color: '#3d5566' }}>{row.country}</td>
                    <td className="px-5 py-4" style={{ color: '#3d5566' }}>{row.type}</td>
                    <td className="px-5 py-4" style={{ color: '#3d5566' }}>{row.focus}</td>
                    <td className="px-5 py-4 font-medium" style={{ color: '#2A7FA5' }}>{row.tuition}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-sm font-body text-center" style={{ color: '#5a6e7a' }}>
            Tuition figures are indicative international rates for 2024–25. Scholarship opportunities through Erasmus+, Spanish Government Awards, Camões Institute, and institutional grants can substantially reduce total costs. Nexus Vantage provides end-to-end consultancy: university selection · application support · visa preparation · post-arrival integration.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: '#2A7FA5' }}>
        <div className="max-w-3xl mx-auto px-6 text-center fade-in-up">
          <h2 className="font-headline font-bold text-white mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)' }}>
            Find Your Perfect University in Portugal
          </h2>
          <p className="font-body text-white/80 mb-8" style={{ fontSize: '1.1rem' }}>
            Our consultants will match you with the right institution based on your profile, goals, and budget.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-10 py-4 font-body font-semibold rounded-lg transition-all duration-300 hover:-translate-y-1"
            style={{ backgroundColor: '#F5EDD6', color: '#2A7FA5', fontSize: '1.05rem' }}
          >
            Book a Free Consultation
          </Link>
        </div>
      </section>
    </div>
  );
}
